import { Text, View, Pressable } from 'react-native';
import { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from "@legendapp/list";
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import Table from '@/components/General/Table/Table';
import Chip from '@/components/General/Chip';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import usePlatform from '@/utils/hooks/usePlatform';
import { formatCampaignDate, senderName, senderProfile, statusColorVariant, shouldFetchDeliveryStatus } from './campaignHelpers';
import CampaignDetailSheet from './CampaignDetailSheet';
import SMSCampaignRecipientsSection from './SMSCampaignRecipientsSection';

/**
 * Email Campaigns tab on the Connect screen — past mass email sends.
 * Native: card list. Web: table. Row press opens a detail sheet with all fields.
 */
const EmailCampaignsList = () => {
    const queryClient = useQueryClient();
    const { isNative } = usePlatform();
    const { data: campaigns, isLoading: loadingCampaigns } = Query.emailCampaigns.all();
    const [searchText, setSearchText] = useState('');
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    const filteredCampaigns = useMemo(() => {
        const all = campaigns || [];
        if(!searchText) return all;
        const q = searchText.toLowerCase();
        return all.filter(c => c.subject?.toLowerCase().includes(q) || senderName(c).toLowerCase().includes(q) || c.status?.toLowerCase().includes(q) || c.Template?.name?.toLowerCase().includes(q));
    }, [campaigns, searchText]);

    const handleRefresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.emailCampaigns.all() });

    const columns = useMemo(() => [
        {
            value: 'Subject',
            accessor: 'subject',
            minWidth: 240,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellTitle} numberOfLines={1}>{item.subject || '(No subject)'}</Text>
                </View>
            ),
        },
        {
            value: 'Sent By',
            accessor: 'Sender.firstName',
            minWidth: 150,
            renderer: (item) => {
                const profile = senderProfile(item);
                const name = senderName(item);
                return (
                    <View style={styles.senderCell}>
                        {profile ? (
                            <Avatar size={26} profile={profile} disableBorder />
                        ) : (
                            <View style={styles.systemAvatar}>
                                <Unicon name="hardware-chip-outline" size={14} colorVariant="textSecondary" />
                            </View>
                        )}
                        <Text style={[styles.cellText, !profile && styles.cellTextSecondary]} numberOfLines={1}>{name}</Text>
                    </View>
                );
            },
        },
        {
            value: 'Date',
            accessor: 'createdAt',
            minWidth: 170,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText} numberOfLines={1}>{item.status === 'scheduled' && item.scheduledFor ? `Scheduled ${formatCampaignDate(item.scheduledFor)}` : formatCampaignDate(item.createdAt)}</Text>
                </View>
            ),
        },
        {
            value: 'Status',
            accessor: 'status',
            minWidth: 110,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Chip colorVariant={statusColorVariant(item.status)} style={styles.statusChip}>{item.status || '—'}</Chip>
                </View>
            ),
        },
        {
            value: 'Template',
            accessor: 'Template.name',
            minWidth: 150,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText} numberOfLines={1}>{item.type == 'template' ? (item.Template?.name || '—') : 'Simple'}</Text>
                </View>
            ),
        },
        {
            value: 'Recipients',
            accessor: 'recipientCount',
            minWidth: 90,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText}>{item.recipientCount ?? 0}</Text>
                </View>
            ),
        },
    ], []);

    const renderItem = ({ item }) => {
        return (
            <Pressable testID={`email-campaign-row-${item.id}`} style={styles.card} onPress={() => setSelectedCampaign(item)}>
                <View style={styles.cardHeader}>
                    <View style={styles.iconWrap}><Unicon name="mail-outline" size={19} colorVariant="connect" /></View>
                    <View style={styles.textContainer}>
                        <Text style={styles.title} numberOfLines={2}>{item.subject || '(No subject)'}</Text>
                        <Text style={styles.subtitle} numberOfLines={1}>{item.status === 'scheduled' && item.scheduledFor ? `Scheduled ${formatCampaignDate(item.scheduledFor)}` : formatCampaignDate(item.createdAt)} · {senderName(item)}</Text>
                    </View>
                    <Unicon name="chevron-forward-outline" size={18} colorVariant="textSecondary" />
                </View>
                <View style={styles.cardMetrics}>
                    {item.sentCount != null && <Chip colorVariant={item.status === 'scheduled' ? 'warning' : 'success'}>{item.sentCount} {item.status === 'scheduled' ? 'scheduled' : 'accepted'}</Chip>}
                    {(item.failedCount ?? 0) > 0 && <Chip colorVariant="error">{item.failedCount} failed</Chip>}
                    {(item.skippedCount ?? 0) > 0 && <Chip colorVariant="warning">{item.skippedCount} skipped</Chip>}
                    {!!item.status && <Chip colorVariant={statusColorVariant(item.status)} style={styles.statusChip}>{item.status}</Chip>}
                </View>
            </Pressable>
        );
    };

    let contentLoaders = [];
    if(loadingCampaigns) {
        for(let i = 0; i < 8; i++) {
            contentLoaders.push(<View key={"loader" + i} style={styles.loaderRow}><ContentLoader containerHeight={60} staggerPosition={i} /></View>);
        }
    }

    return (
        <View style={styles.container}>
            {isNative && (
                <View style={styles.searchContainer}>
                    <SearchBox
                        searchText={searchText}
                        onChangeText={setSearchText}
                        placeholder="Search email campaigns"
                        textChangeDelay={150}
                        testID="EmailCampaignsList"
                    />
                </View>
            )}
            {isNative ? (
                <LegendList
                    recycleItems
                    estimatedItemSize={106}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={handleRefresh}
                    keyExtractor={(item) => item.id}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    data={loadingCampaigns ? [] : filteredCampaigns}
                    renderItem={renderItem}
                    ListFooterComponent={!loadingCampaigns
                        ? <View style={styles.footer}>{filteredCampaigns.length == 0 ? <EmptyListPlaceholder spacing={10} message={searchText ? "No email campaigns match your search" : "No email campaigns yet!"} /> : <View />}</View>
                        : <View style={styles.footerLoading}>{contentLoaders}</View>}
                />
            ) : (
                <Table
                    title="Email Campaigns"
                    tableId="email-campaigns"
                    columns={columns}
                    data={filteredCampaigns}
                    isLoading={loadingCampaigns}
                    onRowClick={(item) => setSelectedCampaign(item)}
                    onRefresh={handleRefresh}
                    onSearchChange={setSearchText}
                    estimatedItemSize={52}
                />
            )}

            <CampaignDetailSheet
                campaign={selectedCampaign}
                onClose={() => setSelectedCampaign(null)}
                modalTitle="Email Campaign"
                icon="mail-outline"
                title={selectedCampaign?.subject || '(No subject)'}
                testID="email-campaign-detail"
                headerChips={selectedCampaign ? [
                    { label: 'Type', value: selectedCampaign.type == 'template' ? 'Template' : 'Simple', icon: selectedCampaign.type == 'template' ? 'document-text-outline' : 'create-outline' },
                    ...(selectedCampaign.templateID && selectedCampaign.Template?.name ? [{ label: 'Template', value: selectedCampaign.Template.name, icon: 'document-attach-outline' }] : []),
                    { label: 'Recipients', value: `${selectedCampaign.recipientCount ?? 0} ${selectedCampaign.sentCount == null ? 'recipients' : 'selected'}`, icon: 'people-outline' },
                    ...(selectedCampaign.scheduledFor ? [{ label: 'Scheduled For', value: formatCampaignDate(selectedCampaign.scheduledFor), icon: 'time-outline' }] : []),
                ] : []}
                stats={selectedCampaign?.sentCount != null ? [
                    { label: selectedCampaign.status === 'scheduled' ? 'Scheduled' : 'Accepted', value: selectedCampaign.sentCount, colorVariant: selectedCampaign.status === 'scheduled' ? 'warning' : 'success' },
                    { label: 'Failed', value: selectedCampaign.failedCount ?? 0, colorVariant: (selectedCampaign.failedCount ?? 0) > 0 ? 'error' : undefined },
                    { label: 'Skipped', value: selectedCampaign.skippedCount ?? 0, colorVariant: (selectedCampaign.skippedCount ?? 0) > 0 ? 'warning' : undefined },
                ] : []}
                recipientsSection={selectedCampaign ? <SMSCampaignRecipientsSection key={selectedCampaign.id} campaignID={selectedCampaign.id} channel="email" hasHistory={selectedCampaign.sentCount != null} deliveryEnabled={shouldFetchDeliveryStatus(selectedCampaign)} /> : null}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
    },
    searchContainer: {
        paddingHorizontal: 8,
        paddingTop: 10,
        paddingBottom: 4,
    },
    card: {
        padding: 12,
        marginHorizontal: 6,
        marginVertical: 5,
        borderRadius: 16,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: 10,
        _web: {
            transition: "all 0.15s ease",
            _hover: { backgroundColor: colors.hoverCard, transform: [{ scale: 1.01 }], boxShadow: shadows.cardHover },
        },
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    iconWrap: {
        width: 38,
        height: 38,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.connectContainer,
    },
    textContainer: {
        flex: 1,
        gap: 3,
        minWidth: 0,
    },
    title: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    subtitle: {
        fontSize: 11,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
    },
    cardMetrics: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        paddingLeft: 48,
    },
    statusChip: {
        textTransform: 'capitalize',
        alignSelf: 'flex-start',
    },
    cellContainer: {
        justifyContent: 'center',
        paddingVertical: 10,
        minHeight: 52,
    },
    senderCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 8,
        minHeight: 52,
    },
    systemAvatar: {
        width: 26,
        height: 26,
        borderRadius: 13,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
    },
    cellTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    cellText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    cellTextSecondary: {
        color: colors.textSecondary,
    },
    loaderRow: {
        marginHorizontal: 10,
        marginVertical: 6,
    },
    footer: {
        paddingBottom: 40,
    },
    footerLoading: {
        paddingBottom: 20,
    },
}));

export default EmailCampaignsList;
